import { getObjectKeys } from "./getObjectKeys";

type TObject = Record<string, any>;

const isObject = (a: any) => a && typeof a === "object" && !Array.isArray(a);
const isArray = (a: any) => a && typeof a === "object" && Array.isArray(a);
/**
 * Merges two objects deeply, combining their properties.
 *
 * @typeParam T - The type of the first object.
 * @typeParam K - The type of the second object.
 * @param obj1 - The first object to merge.
 * @param obj2 - The second object to merge.
 * @returns A new object that is the result of merging `obj1` and `obj2`.
 * @throws An error if a property in `obj1` and `obj2` differs by type.
 */
export const mergeObjects = <T extends TObject, K extends TObject>(
  obj1: T,
  obj2: K,
): K & T => {
  const result: any = structuredClone(obj1);
  getObjectKeys(obj2).forEach(key => {
    if (result[key]) {
      if (isObject(obj2[key]) && isObject(result[key])) {
        result[key] = mergeObjects(obj2[key] as object, result[key]);
      } else if (isArray(obj2[key] && isArray(result[key]))) {
        result[key] = [...result[key], ...(obj2[key] as any[])];
      } else if (typeof obj2[key] === typeof result[key]) {
        result[key] = obj2[key];
      } else {
        throw Error(String(key) + "property differs by type");
      }
    } else {
      result[key] = obj2[key];
    }
  });

  return structuredClone(result);
};
